import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import ReactPaginate, { ReactPaginateProps } from 'react-paginate';
import { ButtonProps, buttonVariants } from '../common/Button';
import { cn } from '@/utils/utils';

interface PaginateProps extends ReactPaginateProps {
    readonly size?: ButtonProps['size'],
    readonly isPagenateEU?: boolean
};

export default function Paginate({
    containerClassName,
    pageLinkClassName,
    activeLinkClassName,
    previousLinkClassName,
    previousLabel,
    nextLinkClassName,
    nextLabel,
    breakLabel,
    breakLinkClassName,
    disabledLinkClassName,
    size = 'icon',
    isPagenateEU = false,
    pageRangeDisplayed = 3,
    marginPagesDisplayed = 1,
    ...props
}: PaginateProps) {
    const paginateLinkClass = buttonVariants({
        variant: 'ghost',
        size
    });

    const currentPage = (props.forcePage ?? 0) + 1;
    const totalPages = props.pageCount ?? 1;

    const paginateEULinkClass = buttonVariants({
        variant: 'paging',
        size,
        className: 'h-[30px] rounded-none text-fs-mid14 leading-lh-mid14 hoverable:hover:border-primaryWeb hoverable:hover:text-primaryWeb hoverable:hover:bg-[#fff]'
    });

    if (isPagenateEU) {
        return (
            <ReactPaginate
                containerClassName={cn('flex flex-row flex-wrap items-center gap-[6px] xsm:gap-[10px]', containerClassName)}
                pageLinkClassName={cn(
                    paginateEULinkClass,
                    'w-fit min-w-[30px] px-2.5 font-medium border border-border-gray',
                    pageLinkClassName
                )}
                activeLinkClassName={cn(
                    buttonVariants({ variant: 'pagingSelected', size }),
                    'bg-border-gray text-customWhite w-fit px-2.5 font-medium',
                    activeLinkClassName
                )}
                previousLinkClassName={cn(paginateEULinkClass, 'w-[30px] bg-primaryWeb text-[#fff] border border-primaryWeb', previousLinkClassName)}
                nextLinkClassName={cn(paginateEULinkClass, 'w-[30px] bg-primaryWeb text-[#fff] border border-primaryWeb', nextLinkClassName)}
                previousLabel={<ChevronLeft className='h-4 w-4' />}
                nextLabel={<ChevronRight className='h-4 w-4' />}
                breakLabel={<MoreHorizontal className='h-4 w-4' />}
                breakLinkClassName={cn('h-[30px] w-4 items-center justify-center hover:text-primaryWeb', breakLinkClassName)}
                disabledLinkClassName={cn('opacity-50 pointer-events-none', disabledLinkClassName)}
                pageRangeDisplayed={pageRangeDisplayed}
                marginPagesDisplayed={marginPagesDisplayed}
                {...props}
            />
        );
    }

    return (
        <>
            {/* ✅ Mobile: < 1 / 7 > */}
            <div className='flex items-center justify-center gap-1 xsm:hidden'>
                <button
                    className={paginateLinkClass}
                    disabled={currentPage <= 1}
                    onClick={() => props.onPageChange?.({ selected: currentPage - 2 })}
                >
                    <ChevronLeft className='h-4 w-4' />
                </button>
                <span className='text-sm font-medium min-w-[50px] text-center'>
                    {currentPage}
                    {' '}
                    /
                    {' '}
                    {totalPages}
                </span>
                <button
                    className={paginateLinkClass}
                    disabled={currentPage >= totalPages}
                    onClick={() => props.onPageChange?.({ selected: currentPage })}
                >
                    <ChevronRight className='h-4 w-4' />
                </button>
            </div>

            {/* ✅ Desktop: ReactPaginate full */}
            <ReactPaginate
                containerClassName={cn('hidden xsm:flex flex-row items-center gap-1', containerClassName)}
                pageLinkClassName={cn(paginateLinkClass, pageLinkClassName, 'h-8 w-fit px-2.5 font-normal')}
                activeLinkClassName={cn(
                    buttonVariants({ variant: 'active', size }),
                    activeLinkClassName,
                    'h-8 w-fit px-2.5 font-normal'
                )}
                previousLinkClassName={cn(paginateLinkClass, 'h-8 w-fit px-2.5', previousLinkClassName)}
                nextLinkClassName={cn(paginateLinkClass, 'h-8 w-fit px-2.5', nextLinkClassName)}
                previousLabel={<ChevronLeft className='h-4 w-4' />}
                nextLabel={<ChevronRight className='h-4 w-4' />}
                breakLabel={<MoreHorizontal className='h-4 w-4' />}
                breakLinkClassName={cn('h-8 w-9 items-center justify-center', breakLinkClassName)}
                disabledLinkClassName={cn('opacity-50 pointer-events-none', disabledLinkClassName)}
                {...props}
            />
        </>
    );
}
